import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBookOpen, FiClipboard, FiTrendingUp, FiSettings, FiAward, FiShield, FiGlobe, FiArrowRight } = FiIcons;

const Services = () => {
  const mainServices = [
    {
      id: 'formation',
      icon: FiBookOpen,
      title: 'Formation Professionnelle',
      description: 'Programmes de formation sur mesure couvrant les normes ISO, les meilleures pratiques managériales et le développement des savoir-faire techniques.',
      features: ['Formations ISO certifiantes', 'Études de cas pratiques', 'Adaptation au contexte local', 'Suivi post-formation']
    },
    {
      id: 'audit',
      icon: FiClipboard,
      title: 'Audit et Évaluation',
      description: 'Audits de conformité, de performance et d\'efficacité basés sur les référentiels internationalement reconnus.',
      features: ['Audit de conformité ISO', 'Évaluation des performances', 'Diagnostic objectif', 'Recommandations actionnables']
    },
    {
      id: 'conseil',
      icon: FiTrendingUp,
      title: 'Conseil Stratégique',
      description: 'Accompagnement dans la définition et la mise en œuvre de stratégies d\'amélioration et l\'optimisation des processus critiques.',
      features: ['Stratégies d\'amélioration', 'Structuration des systèmes', 'Optimisation des processus', 'Gestion du changement']
    },
    {
      id: 'connexes',
      icon: FiSettings,
      title: 'Services Connexes',
      description: 'Assistance à la documentation, facilitation du changement organisationnel, mentorat de managers et assistance à la certification.',
      features: ['Assistance documentation', 'Facilitation du changement', 'Mentorat managers', 'Assistance certification']
    }
  ];

  const isoStandards = [
    {
      standard: 'ISO 9001',
      title: 'Systèmes de management de la qualité',
      color: 'from-blue-500 to-blue-700',
      // Image professionnelle avec un contexte africain (réunion d'équipe/bureau)
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=800'
    },
    {
      standard: 'ISO 14001',
      title: 'Systèmes de management environnemental',
      color: 'from-green-500 to-green-700',
      // Image de nature/paysage africain pour l'environnement
      image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&q=80&w=800'
    },
    {
      standard: 'ISO 45001',
      title: 'Systèmes de management de la santé et de la sécurité au travail',
      color: 'from-red-500 to-red-700',
      image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&q=80&w=800'
    },
    {
      standard: 'ISO 39001',
      title: 'Sécurité routière',
      color: 'from-orange-500 to-orange-700',
      image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&q=80&w=800'
    },
    {
      standard: 'ISO 27001',
      title: 'Sécurité de l\'information',
      color: 'from-purple-500 to-purple-700',
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=800'
    },
    {
      standard: 'ISO 50001',
      title: 'Efficacité énergétique',
      color: 'from-yellow-500 to-yellow-700',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&q=80&w=800'
    }
  ];

  const sectors = [
    'Industrie Manufacturière',
    'Services',
    'Énergie',
    'Santé',
    'Éducation',
    'Secteur Minier',
    'Construction',
    'Entreprises Publiques'
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="pt-16 lg:pt-20"
    >
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-700 dark:from-blue-950 dark:to-blue-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Nos Services
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Solutions intégrées pour votre excellence opérationnelle
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
              Nos Domaines de Compétence
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Quatre axes stratégiques pour votre transformation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all group cursor-pointer"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <SafeIcon icon={service.icon} className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">{service.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300">{service.description}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full"></div>
                      <span className="text-sm text-gray-700 dark:text-gray-200">{feature}</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={`/services/${service.id}`}
                  className="inline-flex items-center text-blue-600 dark:text-blue-400 font-semibold hover:text-blue-800 dark:hover:text-blue-300 transition-colors group-hover:translate-x-2 transition-transform"
                >
                  En Savoir Plus <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ISO Standards */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
              Référentiels d'Expertise
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Maîtrise des normes et référentiels internationaux
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isoStandards.map((iso, index) => (
              <motion.div
                key={index}
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all flex flex-col h-full"
              >
                <div className={`w-full h-2 bg-gradient-to-r ${iso.color} rounded-full mb-4`}></div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{iso.standard}</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4 flex-grow">{iso.title}</p>
                <div className="rounded-lg overflow-hidden h-48 w-full mt-auto group">
                  <img
                    src={iso.image}
                    alt={iso.title}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-4">
              Secteurs d'Activité
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Expertise transversale dans de nombreux domaines
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {sectors.map((sector, index) => (
              <motion.div
                key={index}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-6 text-center shadow-lg hover:shadow-xl transition-all"
              >
                <SafeIcon icon={FiGlobe} className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
                <p className="text-gray-800 dark:text-white font-medium">{sector}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-700 to-blue-900 dark:from-blue-900 dark:to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Besoin d'un Accompagnement Sur Mesure ?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Contactez-nous pour discuter de vos besoins spécifiques
            </p>
            <motion.a
              href="tel:+237699187929"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center px-8 py-4 bg-white text-blue-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiAward} className="mr-2 w-5 h-5" />
              Demander un Devis
            </motion.a>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Services;